import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, ArrowLeft, Plus, Receipt, CaretLeft, CaretRight,
  Lock, X, FloppyDisk, Spinner, ShieldCheck, ClockCounterClockwise,
  PaperPlaneTilt, MagnifyingGlass, Phone, WarningCircle, Trash,
  CurrencyDollar, UserCircle, Tag, Package, CaretDown,
} from '@phosphor-icons/react';
import { API, STATUS_COMANDA, formatCurrency } from './constants';
import { acaoComandaEstorno } from '@/lib/comandaAcoes';

const formatDateBR = (ds) => {
  const d = new Date(ds + 'T12:00:00');
  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
};
const isToday = (ds) => ds === new Date().toISOString().split('T')[0];

// ==================== RETRO AUTH MODAL ====================
const RetroAuthModal = ({ titulo, descricao, onClose, onAuthorized }) => {
  const [step, setStep] = useState('auth');
  const [loginStr, setLoginStr] = useState('');
  const [password, setPassword] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  const handleValidate = async () => {
    if (!loginStr.trim() || !password.trim()) { setError('Preencha login e senha'); return; }
    setValidating(true); setError('');
    try {
      const res = await fetch(`${API}/auth/validate-admin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginStr, password })
      });
      if (!res.ok) { const e = await res.json(); setError(e?.detail || 'Credenciais invalidas'); return; }
      const data = await res.json();
      setAdminUser(data); setStep('justificativa');
    } catch { setError('Erro ao validar'); } finally { setValidating(false); }
  };

  const handleConfirm = () => {
    if (justificativa.trim().length < 5) { setError('Justificativa minimo 5 caracteres'); return; }
    onAuthorized(loginStr, password, justificativa, adminUser);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="retro-auth-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 pb-4 border-b" style={{ borderColor: '#C9A96230' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#C9A96220' }}>
              <ShieldCheck size={22} weight="fill" style={{ color: '#C9A962' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{titulo}</h2>
              <p className="text-[11px] text-[#71717A]">{descricao}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        {step === 'auth' ? (
          <div className="p-6 space-y-4">
            <div className="text-center mb-2">
              <Lock size={32} weight="light" className="mx-auto mb-2" style={{ color: '#C9A962' }} />
              <p className="text-sm text-[#71717A]">Apenas ADMIN pode autorizar operacoes retroativas.</p>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Login</label>
              <input data-testid="input-retro-login" type="text" value={loginStr}
                onChange={(e) => { setLoginStr(e.target.value); setError(''); }}
                placeholder="Login" autoFocus
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Senha</label>
              <input data-testid="input-retro-password" type="password" value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
                placeholder="Senha"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30" />
            </div>
            {error && <div className="flex items-center gap-1.5 text-red-500">
              <WarningCircle size={14} weight="fill" /><span className="text-xs font-medium">{error}</span>
            </div>}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">Cancelar</button>
              <button data-testid="btn-validar-admin" onClick={handleValidate} disabled={validating}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#C9A962' }}>
                {validating ? <Spinner size={16} className="animate-spin" /> : <ShieldCheck size={16} weight="bold" />}
                {validating ? 'Validando...' : 'Autorizar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {adminUser && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F0FDF4] border border-[#1B433220]">
                <ShieldCheck size={14} weight="fill" style={{ color: '#1B4332' }} />
                <span className="text-xs font-medium text-[#1B4332]">Autorizado por: {adminUser.user_name} ({adminUser.user_role})</span>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide mb-1" style={{ color: '#C9A962' }}>Justificativa *</label>
              <textarea data-testid="input-justificativa" value={justificativa}
                onChange={(e) => { setJustificativa(e.target.value); setError(''); }}
                placeholder="Motivo do ajuste retroativo..." rows={3} autoFocus
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border rounded-xl text-sm focus:outline-none resize-none"
                style={{ borderColor: '#C9A96240' }} />
              {error && <div className="flex items-center gap-1.5 mt-2 text-red-500">
                <WarningCircle size={14} weight="fill" /><span className="text-xs font-medium">{error}</span>
              </div>}
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">Cancelar</button>
              <button data-testid="btn-confirmar-retro" onClick={handleConfirm} disabled={justificativa.trim().length < 5}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#C9A962' }}>
                <ShieldCheck size={16} weight="bold" />Confirmar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CLIENTE AUTOCOMPLETE ====================
const ClienteAutocomplete = ({ value, onSelect }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!showDropdown) return;
      setLoading(true);
      try {
        const res = await fetch(`${API}/clientes/search/quick?q=${encodeURIComponent(query)}`);
        setResults(await res.json());
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [query, showDropdown]);

  useEffect(() => {
    const h = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <input
          data-testid="input-cliente"
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); onSelect({ cliente_id: '', cliente_nome: e.target.value }); }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Buscar cliente por nome ou telefone..."
          className="w-full pl-9 pr-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
        />
      </div>
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E4E4E7] rounded-xl shadow-lg max-h-64 overflow-y-auto z-40" data-testid="cliente-dropdown">
          {loading ? (
            <div className="p-4 text-center text-xs text-[#A1A1AA]">Buscando...</div>
          ) : results.length === 0 ? (
            <div className="p-3">
              <p className="text-xs text-[#A1A1AA]">Nenhum cliente encontrado.</p>
              {query.trim() && (
                <p className="text-[11px] text-[#1B4332] mt-1">
                  Sera registrado em nome de "<span className="font-semibold">{query.trim()}</span>" (sem vincular a ficha).
                </p>
              )}
            </div>
          ) : (
            results.map(c => (
              <button
                key={c.id}
                data-testid={`cliente-opt-${c.id}`}
                onClick={() => { setQuery(c.nome); onSelect({ cliente_id: c.id, cliente_nome: c.nome }); setShowDropdown(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F4F4F5] text-left transition-colors"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1B433215' }}>
                  <span className="text-xs font-semibold" style={{ color: '#1B4332' }}>{(c.nome || '?').charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#18181B] truncate">{c.nome}</p>
                  {c.telefone && (
                    <p className="text-[11px] text-[#71717A] flex items-center gap-1">
                      <Phone size={10} weight="bold" />{c.telefone}
                    </p>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ==================== ITEM ROW ====================
const ItemRow = ({ item, index, servicos, produtos, onChange, onRemove }) => {
  const isProduto = item.tipo === 'produto';
  const qty = parseFloat(String(item.quantidade).replace(',', '.')) || 0;
  const val = parseFloat(String(item.valor_unitario).replace(',', '.')) || 0;
  const desc = parseFloat(String(item.desconto).replace(',', '.')) || 0;
  const subtotal = Math.max(qty * val - desc, 0);

  const handleServicoChange = (e) => {
    const id = e.target.value;
    const s = servicos.find(x => x.id === id);
    if (!s) { onChange(index, { ...item, ref_id: '', codigo: '', nome: '', valor_unitario: '' }); return; }
    onChange(index, {
      ...item,
      ref_id: s.id,
      codigo: s.codigo,
      nome: s.nome,
      valor_unitario: String(s.valor_base).replace('.', ','),
    });
  };

  const handleProdutoChange = (e) => {
    const id = e.target.value;
    const p = produtos.find(x => x.id === id);
    if (!p) { onChange(index, { ...item, ref_id: '', codigo: '', nome: '', valor_unitario: '', saldo_atual: 0 }); return; }
    onChange(index, {
      ...item,
      ref_id: p.id,
      codigo: p.sku,
      nome: p.produto,
      valor_unitario: p.custo_unitario ? String(p.custo_unitario).replace('.', ',') : '',
      saldo_atual: p.saldo_atual,
    });
  };

  const saldoTxt = isProduto && item.ref_id ? `Estoque: ${item.saldo_atual ?? 0}` : '';
  const sobrando = isProduto && item.ref_id ? (item.saldo_atual ?? 0) - qty : null;
  const estoqueInsuficiente = sobrando !== null && sobrando < 0;

  return (
    <div data-testid={`item-row-${index}`} className="p-3 bg-[#F7F6F4] rounded-xl border border-[#E4E4E7]">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide flex items-center gap-1"
          style={{ backgroundColor: isProduto ? '#DDE8FF' : '#E8F5E9', color: isProduto ? '#0369A1' : '#1B4332' }}>
          {isProduto ? <Package size={10} weight="bold" /> : <Tag size={10} weight="bold" />}
          {isProduto ? 'Produto' : 'Servico'}
        </span>
        {item.codigo && <span className="text-[10px] text-[#71717A]">{item.codigo}</span>}
        {saldoTxt && (
          <span className={`text-[10px] ${estoqueInsuficiente ? 'text-red-500 font-semibold' : 'text-[#71717A]'}`}>
            • {saldoTxt}
          </span>
        )}
        <div className="ml-auto">
          <button
            data-testid={`btn-remove-item-${index}`}
            onClick={() => onRemove(index)}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-red-500 hover:bg-red-50"
          >
            <Trash size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 sm:col-span-6">
          <label className="block text-[9px] font-medium text-[#A1A1AA] uppercase tracking-wide mb-1">
            {isProduto ? 'Produto (PAC MAN)' : 'Servico'}
          </label>
          {isProduto ? (
            <select
              data-testid={`select-produto-${index}`}
              value={item.ref_id || ''}
              onChange={handleProdutoChange}
              className="w-full px-2.5 py-2 bg-white border border-[#E4E4E7] rounded-lg text-sm focus:outline-none"
            >
              <option value="">Selecione um produto...</option>
              {produtos.map(p => (
                <option key={p.id} value={p.id}>
                  {p.sku} — {p.produto} (est: {p.saldo_atual})
                </option>
              ))}
            </select>
          ) : (
            <select
              data-testid={`select-servico-${index}`}
              value={item.ref_id || ''}
              onChange={handleServicoChange}
              className="w-full px-2.5 py-2 bg-white border border-[#E4E4E7] rounded-lg text-sm focus:outline-none"
            >
              <option value="">Selecione um servico...</option>
              {servicos.map(s => (
                <option key={s.id} value={s.id}>
                  {s.codigo} — {s.nome}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="col-span-4 sm:col-span-2">
          <label className="block text-[9px] font-medium text-[#A1A1AA] uppercase tracking-wide mb-1">Qtd</label>
          <input
            data-testid={`input-qtd-${index}`}
            type="text"
            value={item.quantidade}
            onChange={(e) => onChange(index, { ...item, quantidade: e.target.value })}
            className="w-full px-2.5 py-2 bg-white border border-[#E4E4E7] rounded-lg text-sm text-right focus:outline-none"
          />
        </div>
        <div className="col-span-4 sm:col-span-2">
          <label className="block text-[9px] font-medium text-[#A1A1AA] uppercase tracking-wide mb-1">Valor Un.</label>
          <input
            data-testid={`input-valor-${index}`}
            type="text"
            value={item.valor_unitario}
            onChange={(e) => onChange(index, { ...item, valor_unitario: e.target.value })}
            placeholder="0,00"
            className="w-full px-2.5 py-2 bg-white border border-[#E4E4E7] rounded-lg text-sm text-right focus:outline-none"
          />
        </div>
        <div className="col-span-4 sm:col-span-2">
          <label className="block text-[9px] font-medium text-[#A1A1AA] uppercase tracking-wide mb-1">Desc.</label>
          <input
            data-testid={`input-desc-${index}`}
            type="text"
            value={item.desconto}
            onChange={(e) => onChange(index, { ...item, desconto: e.target.value })}
            placeholder="0,00"
            className="w-full px-2.5 py-2 bg-white border border-[#E4E4E7] rounded-lg text-sm text-right focus:outline-none"
          />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        {estoqueInsuficiente && (
          <span className="text-[11px] text-red-500 font-medium flex items-center gap-1">
            <WarningCircle size={12} weight="fill" />Estoque insuficiente
          </span>
        )}
        <div className="ml-auto text-right">
          <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wide mr-2">Subtotal:</span>
          <span className="text-sm font-semibold" style={{ color: '#1B4332' }} data-testid={`subtotal-${index}`}>
            {formatCurrency(subtotal)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ==================== NOVA COMANDA MODAL ====================
const NovaComandaModal = ({ selectedDate, servicos, produtos, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    data: selectedDate,
    cliente_nome: '',
    cliente_id: '',
    profissional_nome: '',
    itens: [],
    desconto_geral: '',
    observacoes: '',
  });
  const [saving, setSaving] = useState(false);
  const [showRetro, setShowRetro] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [error, setError] = useState('');

  // totals
  const subtotalItens = form.itens.reduce((acc, it) => {
    const q = parseFloat(String(it.quantidade).replace(',', '.')) || 0;
    const v = parseFloat(String(it.valor_unitario).replace(',', '.')) || 0;
    const d = parseFloat(String(it.desconto).replace(',', '.')) || 0;
    return acc + Math.max(q * v - d, 0);
  }, 0);
  const descGeral = parseFloat(String(form.desconto_geral).replace(',', '.')) || 0;
  const total = Math.max(subtotalItens - descGeral, 0);

  const addItem = (tipo) => {
    setForm(f => ({
      ...f,
      itens: [...f.itens, {
        tipo,
        ref_id: '',
        codigo: '',
        nome: '',
        quantidade: '1',
        valor_unitario: '',
        desconto: '',
        saldo_atual: 0,
      }],
    }));
    setShowAddMenu(false);
  };

  const updateItem = (index, newItem) => {
    setForm(f => {
      const copy = [...f.itens];
      copy[index] = newItem;
      return { ...f, itens: copy };
    });
  };

  const removeItem = (index) => {
    setForm(f => ({ ...f, itens: f.itens.filter((_, i) => i !== index) }));
  };

  const validate = () => {
    if (!form.cliente_nome.trim()) return 'Informe o cliente';
    if (form.itens.length === 0) return 'Adicione ao menos um item (servico ou produto)';
    for (let i = 0; i < form.itens.length; i++) {
      const it = form.itens[i];
      if (!it.nome.trim()) return `Item ${i + 1}: selecione ${it.tipo === 'produto' ? 'um produto' : 'um servico'}`;
      const q = parseFloat(String(it.quantidade).replace(',', '.')) || 0;
      if (q <= 0) return `Item ${i + 1}: quantidade deve ser > 0`;
      if (it.tipo === 'produto' && (it.saldo_atual ?? 0) < q) {
        return `Produto ${it.codigo}: estoque insuficiente (disponivel ${it.saldo_atual ?? 0})`;
      }
    }
    return '';
  };

  const buildPayload = () => {
    const itens = form.itens.map(it => ({
      tipo: it.tipo,
      ref_id: it.ref_id || '',
      codigo: it.codigo || '',
      nome: it.nome || '',
      quantidade: parseFloat(String(it.quantidade).replace(',', '.')) || 0,
      valor_unitario: parseFloat(String(it.valor_unitario).replace(',', '.')) || 0,
      desconto: parseFloat(String(it.desconto).replace(',', '.')) || 0,
    }));
    return {
      data: form.data,
      cliente_nome: form.cliente_nome.trim(),
      cliente_id: form.cliente_id || '',
      profissional_nome: form.profissional_nome.trim(),
      profissional_id: '',
      itens,
      desconto_geral: descGeral,
      observacoes: form.observacoes,
    };
  };

  const handleSave = async (retroAuth = null) => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(''); setSaving(true);
    try {
      let url = `${API}/comandas`;
      let body = buildPayload();

      if (retroAuth) {
        url = `${API}/comandas/retroativo`;
        body = {
          admin_login: retroAuth.login,
          admin_password: retroAuth.password,
          justificativa: retroAuth.justificativa,
          comanda: body,
        };
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text();
        let parsed; try { parsed = JSON.parse(text); } catch { parsed = {}; }
        const detail = parsed?.detail;
        if (detail?.code === 'TIME_BLOCKED') { setShowRetro(true); return; }
        setError(typeof detail === 'string' ? detail : (detail?.message || 'Erro ao criar comanda'));
        return;
      }
      onSuccess();
    } catch { setError('Erro de conexao'); }
    finally { setSaving(false); }
  };

  const handleRetroAuthorized = (login, password, justificativa) => {
    setShowRetro(false);
    handleSave({ login, password, justificativa });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="nova-comanda-modal">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7] sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <Receipt size={20} weight="bold" style={{ color: '#1B4332' }} />
              </div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Nova Comanda</h2>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
              <X size={20} weight="bold" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Data</label>
                <input
                  data-testid="input-data" type="date" value={form.data}
                  onChange={(e) => setForm(f => ({ ...f, data: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Profissional</label>
                <input
                  data-testid="input-profissional" type="text" value={form.profissional_nome}
                  onChange={(e) => setForm(f => ({ ...f, profissional_nome: e.target.value }))}
                  placeholder="Nome do profissional"
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Cliente *</label>
              <ClienteAutocomplete
                value={form.cliente_nome}
                onSelect={(sel) => setForm(f => ({ ...f, cliente_nome: sel.cliente_nome, cliente_id: sel.cliente_id }))}
              />
              {form.cliente_id && (
                <p className="text-[10px] text-[#1B4332] mt-1 flex items-center gap-1">
                  <UserCircle size={12} weight="fill" />Vinculado a ficha existente
                </p>
              )}
            </div>

            {/* Itens */}
            <div className="pt-2 border-t border-[#E4E4E7]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#18181B] uppercase tracking-wide">Itens da Comanda</p>
                <div className="relative">
                  <button
                    data-testid="btn-add-item"
                    onClick={() => setShowAddMenu(v => !v)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                    style={{ backgroundColor: '#1B4332' }}
                  >
                    <Plus size={12} weight="bold" />Adicionar item
                    <CaretDown size={10} weight="bold" />
                  </button>
                  {showAddMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-[#E4E4E7] rounded-xl shadow-lg z-30 min-w-[180px]">
                      <button
                        data-testid="btn-add-servico"
                        onClick={() => addItem('servico')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#F4F4F5] text-left text-sm"
                      >
                        <Tag size={14} weight="bold" style={{ color: '#1B4332' }} />Servico
                      </button>
                      <button
                        data-testid="btn-add-produto"
                        onClick={() => addItem('produto')}
                        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#F4F4F5] text-left text-sm border-t border-[#E4E4E7]"
                      >
                        <Package size={14} weight="bold" style={{ color: '#0369A1' }} />Produto (PAC MAN)
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {form.itens.length === 0 ? (
                <div className="py-6 text-center bg-[#F7F6F4] rounded-xl border border-dashed border-[#E4E4E7]">
                  <p className="text-xs text-[#A1A1AA]">Nenhum item adicionado. Clique em "+ Adicionar item".</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {form.itens.map((it, i) => (
                    <ItemRow
                      key={i}
                      item={it}
                      index={i}
                      servicos={servicos}
                      produtos={produtos}
                      onChange={updateItem}
                      onRemove={removeItem}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#E4E4E7]">
              <div>
                <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Desconto Geral (R$)</label>
                <input
                  data-testid="input-desconto-geral" type="text" value={form.desconto_geral}
                  onChange={(e) => setForm(f => ({ ...f, desconto_geral: e.target.value }))}
                  placeholder="0,00"
                  className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm text-right focus:outline-none"
                />
              </div>
              <div className="flex items-end">
                <div className="w-full p-2.5 rounded-xl border-2" style={{ borderColor: '#1B433230' }}>
                  <p className="text-[10px] text-[#71717A] uppercase">Total</p>
                  <p className="text-xl font-bold" style={{ fontFamily: 'Outfit', color: '#1B4332' }} data-testid="total-comanda">
                    {formatCurrency(total)}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Observacoes</label>
              <textarea
                data-testid="input-observacoes" value={form.observacoes}
                onChange={(e) => setForm(f => ({ ...f, observacoes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm resize-none focus:outline-none"
              />
            </div>

            {error && (
              <div className="flex items-center gap-1.5 text-red-500 px-3 py-2 bg-red-50 rounded-lg">
                <WarningCircle size={14} weight="fill" /><span className="text-xs font-medium">{error}</span>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">
                Cancelar
              </button>
              <button
                data-testid="btn-salvar-comanda"
                onClick={() => handleSave()}
                disabled={saving || !form.cliente_nome.trim() || form.itens.length === 0}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 hover:shadow-lg"
                style={{ backgroundColor: '#1B4332' }}
              >
                {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showRetro && (
        <RetroAuthModal
          titulo="Comanda Retroativa"
          descricao="Criar comanda em data passada requer autorizacao"
          onClose={() => setShowRetro(false)}
          onAuthorized={handleRetroAuthorized}
        />
      )}
    </>
  );
};

// ==================== COMANDA DETAILS DROPDOWN ====================
const ComandaCard = ({ c, onEnviar, onCancelar, onCancelarEstorno }) => {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS_COMANDA[c.status] || STATUS_COMANDA.aberta;
  const hasProdutos = (c.itens || []).some(i => i.tipo === 'produto');

  return (
    <div
      data-testid={`comanda-${c.codigo}`}
      className="salonup-card cursor-default hover:transform-none group overflow-hidden"
    >
      <div className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: st.bg }}>
          <Receipt size={18} weight="bold" style={{ color: st.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-xs font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{c.codigo}</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide" style={{ backgroundColor: st.bg, color: st.color }}>
              {st.label}
            </span>
            {c.retroativo && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase" style={{ backgroundColor: '#C9A96215', color: '#C9A962' }}>
                <ClockCounterClockwise size={10} weight="bold" />Retro
              </span>
            )}
            {hasProdutos && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase" style={{ backgroundColor: '#DDE8FF', color: '#0369A1' }}>
                <Package size={10} weight="bold" />Produtos
              </span>
            )}
          </div>
          <p className="text-sm text-[#18181B] truncate">
            {c.cliente_nome}{c.profissional_nome ? ` — ${c.profissional_nome}` : ''}
          </p>
          <p className="text-[11px] text-[#A1A1AA]">
            {(c.itens || []).length} {((c.itens || []).length === 1) ? 'item' : 'itens'} •{' '}
            <button
              data-testid={`btn-toggle-${c.codigo}`}
              onClick={() => setExpanded(v => !v)}
              className="text-[#1B4332] hover:underline"
            >
              {expanded ? 'ocultar' : 'ver detalhes'}
            </button>
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#1B4332' }}>
            {formatCurrency(c.total)}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-1 flex-shrink-0 max-w-[45%] sm:max-w-none">
          {['enviada_ao_caixa', 'paga'].includes(c.status) && (
            <button data-testid={`btn-cancelar-estorno-${c.codigo}`} onClick={() => onCancelarEstorno(c)}
              className="min-h-[36px] px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-red-200 text-red-700 hover:bg-red-50">
              Cancelar
            </button>
          )}
          {['enviada_ao_caixa', 'paga'].includes(c.status) && (
            <button data-testid={`btn-corrigir-${c.codigo}`} onClick={() => onCancelarEstorno(c, true)}
              className="min-h-[36px] px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#E4E4E7] text-[#1B4332] hover:bg-[#F0FDF4]">
              Corrigir
            </button>
          )}
          {c.status === 'aberta' && (
            <>
              <button
                data-testid={`btn-enviar-${c.codigo}`}
                onClick={() => onEnviar(c.id)}
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-white flex items-center gap-1"
                style={{ backgroundColor: '#0369A1' }}
              >
                <PaperPlaneTilt size={12} weight="bold" />Enviar ao Caixa
              </button>
              <button
                data-testid={`btn-cancelar-${c.codigo}`}
                onClick={() => onCancelar(c.id)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100"
              >
                <Trash size={14} weight="bold" />
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#E4E4E7] bg-[#F7F6F4] px-4 py-3" data-testid={`detalhes-${c.codigo}`}>
          <div className="space-y-1.5">
            {(c.itens || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase"
                  style={{ backgroundColor: it.tipo === 'produto' ? '#DDE8FF' : '#E8F5E9', color: it.tipo === 'produto' ? '#0369A1' : '#1B4332' }}>
                  {it.tipo === 'produto' ? 'PROD' : 'SRV'}
                </span>
                <span className="text-[#71717A]">{it.codigo}</span>
                <span className="text-[#18181B] flex-1 truncate">{it.nome}</span>
                <span className="text-[#A1A1AA]">{it.quantidade}x {formatCurrency(it.valor_unitario)}</span>
                <span className="font-semibold text-[#18181B]">{formatCurrency(it.subtotal)}</span>
              </div>
            ))}
            {c.desconto_geral > 0 && (
              <div className="flex items-center justify-between text-xs text-[#C9A962] pt-1 border-t border-[#E4E4E7]">
                <span>Desconto geral</span>
                <span>- {formatCurrency(c.desconto_geral)}</span>
              </div>
            )}
          </div>
          {c.observacoes && (
            <p className="text-[11px] text-[#71717A] mt-2 italic">"{c.observacoes}"</p>
          )}
          {c.retroativo && c.retroativo_justificativa && (
            <p className="text-[10px] mt-2" style={{ color: '#C9A962' }}>
              <ShieldCheck size={10} weight="fill" className="inline mr-1" />
              Retroativo: {c.retroativo_justificativa}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PAGE ====================
export const ComandasPage = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [comandas, setComandas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNova, setShowNova] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [produtos, setProdutos] = useState([]);

  const fetchComandas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/comandas?data=${selectedDate}`);
      setComandas(await res.json());
    } catch { setComandas([]); } finally { setLoading(false); }
  }, [selectedDate]);

  const fetchCatalog = useCallback(async () => {
    try {
      const [sRes, eRes] = await Promise.all([
        fetch(`${API}/servicos/ativos`),
        fetch(`${API}/pacman/estoque`),
      ]);
      setServicos(await sRes.json());
      setProdutos(await eRes.json());
    } catch { /* */ }
  }, []);

  useEffect(() => { fetchComandas(); }, [fetchComandas]);
  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const changeDate = (d) => {
    const dt = new Date(selectedDate + 'T12:00:00');
    dt.setDate(dt.getDate() + d);
    setSelectedDate(dt.toISOString().split('T')[0]);
  };

  const handleEnviarCaixa = async (id) => {
    const res = await fetch(`${API}/comandas/${id}/enviar-caixa`, { method: 'PUT' });
    if (!res.ok) { const e = await res.json(); alert(e?.detail || 'Erro'); return; }
    fetchComandas();
  };

  const handleCancelarEstorno = async (c, corrigir = false) => {
    await acaoComandaEstorno(API, c, corrigir);
    fetchComandas();
  };

  const handleCancelar = async (id) => {
    if (!window.confirm('Cancelar esta comanda?')) return;
    await fetch(`${API}/comandas/${id}/cancelar`, { method: 'PUT' });
    fetchComandas();
  };

  const todayFlag = isToday(selectedDate);

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="comandas-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Comandas</span>
          </p>
        </div>

        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <Receipt size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">Comandas</h1>
                  <span className="salonup-badge">OPERACIONAL</span>
                </div>
                <p className="salonup-text-sm mt-1">Multiplos servicos e produtos por comanda. Estoque baixa apenas no recebimento do caixa.</p>
              </div>
            </div>
            <button
              data-testid="btn-nova-comanda"
              onClick={() => setShowNova(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}
            >
              <Plus size={16} weight="bold" />Nova Comanda
            </button>
          </div>
        </section>

        <section className="mb-5 animate-fade-in-up">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button data-testid="btn-prev-day" onClick={() => changeDate(-1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white">
                <CaretLeft size={16} weight="bold" />
              </button>
              <div className="text-center min-w-[200px]">
                <h2 className="text-lg font-semibold text-[#18181B] capitalize" style={{ fontFamily: 'Outfit' }} data-testid="current-date">
                  {formatDateBR(selectedDate)}
                </h2>
                {todayFlag && <span className="text-[10px] font-semibold text-[#1B4332] uppercase tracking-wide">Hoje</span>}
              </div>
              <button data-testid="btn-next-day" onClick={() => changeDate(1)}
                className="w-9 h-9 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white">
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
            <input data-testid="date-picker" type="date" value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none" />
          </div>
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : comandas.length === 0 ? (
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <Receipt size={36} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>Nenhuma comanda</h3>
            <p className="text-sm text-[#71717A]">Clique em "Nova Comanda" para registrar.</p>
          </div>
        ) : (
          <div className="space-y-3 animate-fade-in-up" data-testid="comandas-list">
            {comandas.map(c => (
              <ComandaCard key={c.id} c={c} onEnviar={handleEnviarCaixa} onCancelar={handleCancelar} onCancelarEstorno={handleCancelarEstorno} />
            ))}
          </div>
        )}

        <footer className="salonup-footer animate-fade-in mt-8">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
        </footer>
      </div>

      {showNova && (
        <NovaComandaModal
          selectedDate={selectedDate}
          servicos={servicos}
          produtos={produtos}
          onClose={() => setShowNova(false)}
          onSuccess={() => { setShowNova(false); fetchComandas(); fetchCatalog(); }}
        />
      )}
    </div>
  );
};

export default ComandasPage;
